'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';

interface Game {
  id: number;
  name: string;
  image: string;
  g_code?: string;
  p_code?: string;
  cate_code?: string;
  category_id?: number;
  provider_id?: number;
  active?: number;
  is_hot?: number;
  is_new?: number;
}

interface GameSectionProps {
  title: string;
  games: Game[];
  launchingGames: Set<number>;
  canLaunchGame: (game: Game) => boolean;
  onGameLaunch: (game: Game) => void;
  getLotteryRoute?: (id: number) => string;
  isLottery?: boolean;
  sectionType?: 'lottery' | 'hot' | 'buffalo' | 'new';
}

const GameSection: React.FC<GameSectionProps> = ({
  title,
  games,
  launchingGames,
  canLaunchGame,
  onGameLaunch,
  getLotteryRoute,
  isLottery = false,
  sectionType = 'hot'
}) => {
  const router = useRouter();
  const { refreshBalance } = useAuth();
  
  if (!games || games.length === 0) {
    return null;
  }

  return (
    <section className={`games-section ${sectionType}-games`}>
      <div className="section-header">
        <h3 className="section-title">{title}</h3>
      </div>
      <div className="games-grid">
        {games.map((game) => (
          <Card key={game.id} className="game-card" hover glow>
            {isLottery && getLotteryRoute ? (
              <div 
                className="lottery-game-card-content"
                onClick={async () => {
                  console.log('Lottery game clicked:', game.id, game.name);
                  // Refresh balance before navigation
                  await refreshBalance();
                  const route = getLotteryRoute(game.id);
                  console.log('Navigating to:', route);
                  router.push(route);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="lottery-game-image-container">
                  <Image
                    src={game.image}
                    alt={game.name}
                    width={200}
                    height={150}
                    className="lottery-game-image"
                  />
                  <div className="game-overlay"></div>
                  {game.is_hot && <div className="game-badge">HOT</div>}
                  {game.is_new && <div className="game-badge">NEW</div>}
                  {canLaunchGame(game) && (
                    <div className="launch-badge">🎮</div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="game-image-container">
                  <Image
                    src={game.image}
                    alt={game.name}
                    width={200}
                    height={150}
                    className="game-image"
                  />
                  <div className="game-overlay"></div>
                  {game.is_hot && <div className="game-badge">HOT</div>}
                  {game.is_new && <div className="game-badge">NEW</div>}
                  {canLaunchGame(game) && (
                    <div className="launch-badge">🎮</div>
                  )}
                </div>
                <h4 className="game-title">{game.name}</h4>
                <Button 
                  variant="casino" 
                  size="sm" 
                  className="game-button"
                  onClick={() => onGameLaunch(game)}
                  disabled={launchingGames.has(game.id)}
                >
                  {launchingGames.has(game.id) ? (
                    <>
                      <span className="loading-spinner-small"></span>
                      Loading...
                    </>
                  ) : (
                    canLaunchGame(game) ? 'Play' : 'Browse Games'
                  )}
                </Button>
              </>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
};

export default GameSection;
